import React, { ChangeEvent, useMemo } from 'react';
import Link from '@fff-web/fff-ui-shared/Link';
import { getPriceByPlanCode } from '../utils/planPrices';
import styles from './CheckOutDisclaimer.module.css';
import { SEASONAL_PLAN_CODE } from '../../../utils/constants';
import LoadingSection from '../../Shared/LoadingSection/LoadingSection';
import Checkbox from '../../Shared/Checkbox/Checkbox';
import { usePricesContext } from '../../../ContextProviders/PricesContext';

import {
  TEST_ID_INTRO_PRODUCT_DISCLAIMER,
  TEST_ID_SUSCRIBABLE_DISCLAIMER,
  TEST_ID_SHIPPING_DISCLAIMER,
} from './tests/CheckOutDisclaimerTestIds';

const PRICE_INCREASE = '$59.99';
const orderLegalText =
  // eslint-disable-next-line max-len
  'Shipping fees apply to non-contiguous US shipments and orders under $49. Foreign exchange or transaction fees may apply.';
// eslint-disable-next-line max-len
const BILLING_SHIPPING_SCHEDULE_URL = 'https://support.fabfitfun.com/?a=BillingShipping-Schedule---id--fQP9JA7KS6-9PdDGQIL0oQ';

// eslint-disable-next-line max-len
const REFILL_INTRO_CHECKBOX_LABEL = 'I understand and agree that my purchase includes a recurring FabFitFun Membership. I also understand and agree that my purchase includes a recurring product subscription.';
// eslint-disable-next-line max-len
const INTRO_CHECKBOX_LABEL = 'I understand and agree that my purchase includes a recurring FabFitFun Membership.';

interface PriceProps {
  loading: boolean;
  price: number | undefined;
  width: number;
}

const Price: React.FunctionComponent<PriceProps> = ({ loading, price, width }) => (
  <>{!loading && price ? `$${price}` : <LoadingSection height={14} width={width} />}</>
);

interface CheckOutDisclaimerProps {
  disclaimerPriceIncreaseFF?: boolean;
  subscribable?: boolean;
  hasIntro?: boolean;
  acceptedTerms?: boolean;
  setAcceptedTerms?: (value: boolean) => void;
}

const CheckOutDisclaimer: React.FunctionComponent<CheckOutDisclaimerProps> = ({
  disclaimerPriceIncreaseFF,
  subscribable,
  hasIntro,
  acceptedTerms,
  setAcceptedTerms,
}) => {
  const { prices, loading } = usePricesContext();
  const seasonalPrice = getPriceByPlanCode(prices, SEASONAL_PLAN_CODE);

  const handleSetAcceptedTerms = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (setAcceptedTerms) {
      setAcceptedTerms(checked);
    }
  };

  const getCheckboxLabel = useMemo(() => {
    if (hasIntro && subscribable) return REFILL_INTRO_CHECKBOX_LABEL;
    return INTRO_CHECKBOX_LABEL;
  }, [hasIntro, subscribable]);

  return (
    <>
      <p
        className={styles.orderLegalTextLight}
        data-testid={TEST_ID_SHIPPING_DISCLAIMER}
      >
        {orderLegalText}
      </p>
      {(subscribable || hasIntro) && (
        <div
          className={styles.disclaimerContainer}
          data-testid={TEST_ID_SUSCRIBABLE_DISCLAIMER}
        >
          {hasIntro && (
            <>
              <h1>About Your Membership</h1>
              <p
                className={styles.codContainer}
                data-testid={TEST_ID_INTRO_PRODUCT_DISCLAIMER}
              >
                Your Seasonal Membership starts with your initial product purchase
                (but does not include the current Seasonal Box). Your membership
                continues and you will be automatically charged
                {' '}
                {disclaimerPriceIncreaseFF ? (
                  PRICE_INCREASE
                ) : (
                  <Price
                    loading={loading}
                    price={seasonalPrice?.price}
                    width={35}
                  />
                )}
                {' '}
                each season thereafter. Choose the Seasonal Box or credit to shop
                our sales. Cancel any time before your next
                {' '}
                <Link href={BILLING_SHIPPING_SCHEDULE_URL} text="bill date" /> on
                the “My Account” page or by calling 1.855.313.6267.
              </p>
            </>
          )}
          {subscribable && (
            <>
              <h1>About Your Product Subscriptions</h1>
              <p data-testid={TEST_ID_SUSCRIBABLE_DISCLAIMER} className={styles.aboutYourSubscription}>
                Product subscriptions are ongoing and will be automatically charged
                and shipped with your Seasonal Box unless you cancel before your
                next billing date. Cancel on the “My Subscriptions” page or by
                calling 1.855.313.6267.
              </p>
            </>
          )}
          {hasIntro && (
            <div className={styles.checkboxContainer}>
              <Checkbox
                label={getCheckboxLabel}
                handleChange={handleSetAcceptedTerms}
                value={acceptedTerms ?? false}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CheckOutDisclaimer;
